// Client-side view of what RepBuddy remembers.
//
// The backend (services/userMemory.js) owns the reducer and the rules. This
// module only re-derives the small "usual" summary from a stored blob so the
// pre-search card can pre-select before any round trip, and turns the avoid
// list into exclude keywords for the search post-filter. The thresholds match
// the backend: usual means chosen at least twice, or within the last 30 days.

const USUAL_MIN_COUNT = 2;
const USUAL_RECENT_MS = 30 * 24 * 60 * 60 * 1000;
const TOP_N = 5;

function usualOf(tally, now, n = TOP_N) {
  if (!tally || typeof tally !== 'object') return [];
  return Object.values(tally)
    .filter(v => v && (v.n >= USUAL_MIN_COUNT || (now - (v.last || 0)) < USUAL_RECENT_MS))
    .sort((a, b) => (b.n - a.n) || ((b.last || 0) - (a.last || 0)))
    .slice(0, n)
    .map(v => ({ value: v.display || '', n: v.n }))
    .filter(v => v.value);
}

function single(s) {
  return s && s.n >= USUAL_MIN_COUNT ? s.value : null;
}

/** Same shape the backend's summarizeForPrompt returns. Safe on null. */
export function summarizeMemory(memory, now = Date.now()) {
  const u = memory?.usual || {};
  return {
    usualTitles:        usualOf(u.titles, now),
    usualIndustries:    usualOf(u.industries, now),
    usualStates:        usualOf(u.states, now),
    usualCompanies:     usualOf(u.companies, now),
    usualSeniority:     usualOf(u.seniority, now, 1)[0]?.value || null,
    usualEmployeeRange: usualOf(u.employeeRange, now, 1)[0]?.value || null,
    usualCap:           single(u.cap),
    usualPool:          single(u.pool),
    // 'any' is remembered (it is how a rep un-learns a ceiling) but never pre-fills the card.
    usualRevenueRange:  single(u.revenueRange) === 'any' ? null : single(u.revenueRange),
    avoidCompanies:     (memory?.avoidCompanies || []).map(a => a?.name).filter(Boolean),
    notes:              (memory?.notes || []).map(n => n?.text).filter(Boolean),
    taken:              usualOf(memory?.taken?.companies, now, 5).map(t => t.value),
    searches:           memory?.stats?.searches || 0,
  };
}

export function isMemoryEmpty(summary) {
  if (!summary) return true;
  return !(summary.usualTitles?.length || summary.usualIndustries?.length || summary.usualStates?.length ||
           summary.usualCompanies?.length || summary.usualSeniority || summary.usualEmployeeRange ||
           summary.usualCap || summary.usualPool || summary.usualRevenueRange || summary.avoidCompanies?.length ||
           summary.notes?.length || summary.taken?.length);
}

/** Avoid list as exclude keywords for the search post-filter (matches company name). */
export function memoryExcludeKeywords(summary) {
  return (summary?.avoidCompanies || []).map(n => String(n).trim().toLowerCase()).filter(Boolean);
}
